import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Warehouse, Plus, Edit2, Trash2, Search, AlertTriangle, TrendingDown } from 'lucide-react'
import { useInventoryStore } from '../store/inventoryStore'

export default function InventoryManagementPage() {
  const navigate = useNavigate()
  const warehouses = useInventoryStore((state) => state.warehouses)
  const addWarehouse = useInventoryStore((state) => state.addWarehouse)
  const updateWarehouse = useInventoryStore((state) => state.updateWarehouse)
  const deleteWarehouse = useInventoryStore((state) => state.deleteWarehouse)

  const [searchTerm, setSearchTerm] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    capacity: 0,
    currentLoad: 0,
    zones: 0,
    manager: ''
  })

  const filteredWarehouses = useMemo(() => {
    return warehouses.filter(warehouse =>
      warehouse.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      warehouse.location.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }, [warehouses, searchTerm])

  const handleAddWarehouse = () => {
    if (formData.name && formData.location) {
      addWarehouse(formData)
      setFormData({
        name: '',
        location: '',
        capacity: 0,
        currentLoad: 0,
        zones: 0,
        manager: ''
      })
      setShowAddModal(false)
    }
  }

  const handleEditWarehouse = (warehouse) => {
    setSelectedWarehouse(warehouse.id)
    setFormData(warehouse)
    setShowAddModal(true)
  }

  const handleUpdateWarehouse = () => {
    if (selectedWarehouse && formData.name && formData.location) {
      updateWarehouse(selectedWarehouse, formData)
      setFormData({
        name: '',
        location: '',
        capacity: 0,
        currentLoad: 0,
        zones: 0,
        manager: ''
      })
      setSelectedWarehouse(null)
      setShowAddModal(false)
    }
  }

  const handleDeleteWarehouse = (id) => {
    if (confirm('Are you sure you want to delete this warehouse?')) {
      deleteWarehouse(id)
    }
  }

  const closeModal = () => {
    setShowAddModal(false)
    setSelectedWarehouse(null)
    setFormData({
      name: '',
      location: '',
      capacity: 0,
      currentLoad: 0,
      zones: 0,
      manager: ''
    })
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900">
      <div className="space-y-6 p-8">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate(-1)} className="text-blue-400 hover:text-blue-300">←</button>
            <h1 className="text-4xl font-bold text-white flex items-center space-x-3">
              <Warehouse className="w-8 h-8 text-cyan-400" />
              <span>Inventory Management</span>
            </h1>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
          >
            <Plus className="w-5 h-5" />
            <span>Add Warehouse</span>
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-3 w-5 h-5 text-blue-400" />
          <input
            type="text"
            placeholder="Search warehouses by name or location..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-slate-800 border border-blue-500/20 rounded-lg pl-10 pr-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
          />
        </div>

        {/* Warehouse Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
          {filteredWarehouses.map((warehouse) => {
            const utilizationPercent = (warehouse.currentLoad / warehouse.capacity) * 100
            return (
              <div key={warehouse.id} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all shadow-lg">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">{warehouse.name}</h3>
                    <p className="text-blue-300 text-sm">{warehouse.location}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditWarehouse(warehouse)}
                      className="text-blue-400 hover:text-blue-300 p-2 hover:bg-blue-500/10 rounded transition-all"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteWarehouse(warehouse.id)}
                      className="text-red-400 hover:text-red-300 p-2 hover:bg-red-500/10 rounded transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Capacity Meter */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-blue-300 text-sm font-semibold">Capacity Utilization</span>
                      <span className={`text-sm font-bold ${utilizationPercent > 85 ? 'text-red-400' : 'text-green-400'}`}>
                        {utilizationPercent.toFixed(0)}%
                      </span>
                    </div>
                    <div className="w-full bg-slate-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${
                          utilizationPercent > 85 ? 'bg-red-500' : 'bg-green-500'
                        }`}
                        style={{width: `${utilizationPercent}%`}}
                      ></div>
                    </div>
                  </div>

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-blue-300 text-xs">Capacity</p>
                      <p className="text-white font-bold">{warehouse.capacity.toLocaleString()} units</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-blue-300 text-xs">Current Load</p>
                      <p className="text-white font-bold">{warehouse.currentLoad.toLocaleString()} units</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-blue-300 text-xs">Available</p>
                      <p className="text-green-400 font-bold">{(warehouse.capacity - warehouse.currentLoad).toLocaleString()} units</p>
                    </div>
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-blue-300 text-xs">Zones</p>
                      <p className="text-white font-bold">{warehouse.zones}</p>
                    </div>
                  </div>

                  <div className="bg-slate-700/50 rounded-lg p-3">
                    <p className="text-blue-300 text-xs mb-1">Manager</p>
                    <p className="text-white font-semibold">{warehouse.manager}</p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>

        {/* Add/Edit Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
              <h2 className="text-2xl font-bold text-white mb-4">
                {selectedWarehouse ? 'Edit Warehouse' : 'Add New Warehouse'}
              </h2>
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Warehouse Name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Location"
                  value={formData.location}
                  onChange={(e) => setFormData({...formData, location: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Total Capacity"
                  value={formData.capacity}
                  onChange={(e) => setFormData({...formData, capacity: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Current Load"
                  value={formData.currentLoad}
                  onChange={(e) => setFormData({...formData, currentLoad: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="number"
                  placeholder="Number of Zones"
                  value={formData.zones}
                  onChange={(e) => setFormData({...formData, zones: parseInt(e.target.value)})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
                <input
                  type="text"
                  placeholder="Manager Name"
                  value={formData.manager}
                  onChange={(e) => setFormData({...formData, manager: e.target.value})}
                  className="w-full bg-slate-700 border border-blue-500/20 rounded-lg px-4 py-2 text-white placeholder-slate-400 focus:outline-none focus:border-blue-400/50"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeModal}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-lg transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={selectedWarehouse ? handleUpdateWarehouse : handleAddWarehouse}
                  className="flex-1 bg-blue-600 hover:bg-blue-500 text-white px-4 py-2 rounded-lg transition-all"
                >
                  {selectedWarehouse ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
